import { ArrowDown, ArrowUp, theme } from '@dtx-company/shared-components/src'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { DashboardSlug } from '../../../types/reports.types'
import { Drawer } from '@dtx-company/shared-components/src/components/organisms/Drawer'
import { DrawerTypes } from '@dtx-company/inter-app/src/redux/slices/drawer'
import { FC, Fragment, useState } from 'react'
import { FlowlyticsVideo } from './FlowlyticsVideo'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { ReportsFAQDataType } from '@dtx-company/true-common/src/types/prismic'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TextLink } from '@dtx-company/inter-app/src/utils/links'
import { asText } from '@prismicio/client'
import { launch } from '@app/code/src/assets'
import { reverseEnumLookup } from '@dtx-company/true-common/src/utils/types/reverseEnumLookup'
import { useDrawer } from '@dtx-company/inter-app/src/redux/slices/utils/drawer'
import { useRouter } from 'next/router'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowlytics'

const slugArray = [Routes.REPORTS, ...Object.values(DashboardSlug)] as const
type SectionValue = (typeof slugArray)[number]

export const FAQDrawer: FC<{ drawerContent: ReportsFAQDataType }> = ({ drawerContent }) => {
  const { drawerOpen, setClose } = useDrawer(DrawerTypes.FLOWLYTICS_GETTING_STARTED)
  const router = useRouter()
  const dashboardSlug = reverseEnumLookup(DashboardSlug, router.query.dashboard_slug)
  const initialOpenSection = dashboardSlug ? DashboardSlug[dashboardSlug] : Routes.REPORTS
  const [openSection, setOpenSection] = useState<SectionValue | ''>(initialOpenSection)
  const [playing, setPlaying] = useState<boolean>()

  return (
    <Drawer
      open={drawerOpen}
      onClose={() => {
        events.Flowlytics_GettingStarted_Opened_FAQ_Drawer({ open: false })
        setClose()
        setOpenSection(initialOpenSection)
      }}
      width={'100%'}
    >
      {drawerContent !== undefined && (
        <Box mt={'24px'} flexDirection="column" width="100%">
          <Box mx={'24px'} justifyContent={'space-between'}>
            <Text variant={'header/page'}>{asText(drawerContent.drawer_header)}</Text>
          </Box>
          <Spacer my={'8px'} />
          {drawerContent.accordion_sections.map(
            ({ accordion_header, video_link, article_title, article_link, page_route }, idx) => {
              const sectionHeader = asText(accordion_header)
              const articleTitle = asText(article_title)
              const slug = asText(page_route) as SectionValue
              const toggleSection = (isOpen: boolean): void => {
                if (isOpen) return setOpenSection('')
                events.Flowlytics_GettingStarted_Clicked_Section({ section: sectionHeader })
                setOpenSection(slug)
              }
              return (
                (video_link?.url || article_link?.url) && (
                  <Fragment key={idx}>
                    <Box
                      tabIndex={0}
                      cursor="pointer"
                      justifyContent={'space-between'}
                      py={'12px'}
                      style={{ borderTop: `1px solid ${theme.colors.secondary.border}` }}
                      onClick={() => {
                        setPlaying(false)
                        toggleSection(openSection === slug)
                      }}
                      onKeyDown={e => e.key === 'Enter' && toggleSection(openSection === slug)}
                    >
                      <Box mx={'24px'}>
                        <Text variant={'chart/title'}>{sectionHeader}</Text>
                      </Box>
                      <Box mx="24px">
                        {openSection === slug ? (
                          <ArrowUp color={theme.colors.primary.flowBlue} />
                        ) : (
                          <ArrowDown color={theme.colors.primary.flowBlue} />
                        )}
                      </Box>
                    </Box>
                    <Box
                      mx={'24px'}
                      height={'max-content'}
                      collapsible={{
                        open: openSection === slug,
                        maxHeight: 332
                      }}
                    >
                      <Box
                        mb="8px"
                        flexDirection="column"
                        visibility={openSection === slug ? 'visible' : 'hidden'}
                      >
                        {video_link?.url && (
                          <FlowlyticsVideo
                            playing={playing}
                            videoLink={video_link.url}
                            videoCallback={() => {
                              setPlaying(true)
                              events.Flowlytics_GettingStarted_Clicked_Video({
                                videoName: sectionHeader
                              })
                            }}
                          />
                        )}
                        {article_link?.url && (
                          <Box key={idx} my="24px">
                            <TextLink
                              tabIndex={0}
                              variant={'accordion/secondary'}
                              href={article_link.url}
                              isExternal
                              color={theme.colors.primary.flowBlue}
                              onClick={() => {
                                events.Flowlytics_GettingStarted_Clicked_FAQ({
                                  linkName: articleTitle
                                })
                              }}
                            >
                              Article: {articleTitle} <Icon src={launch} verticalAlign="middle" />
                            </TextLink>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Fragment>
                )
              )
            }
          )}
          <Spacer
            mb={'24px'}
            style={{ borderBottom: `1px solid ${theme.colors.secondary.border}` }}
          />
        </Box>
      )}
    </Drawer>
  )
}
